import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Heart, Leaf, Users } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CSRPage() {
  const [csrItems, setCsrItems] = useState([]);

  // Helper function to get proper image URL
  const getImageUrl = (url) => {
    if (!url) return '';
    return url.startsWith('/api/images/') 
      ? `${process.env.REACT_APP_BACKEND_URL}${url}/data` 
      : url;
  };

  useEffect(() => {
    const fetchCSR = async () => {
      try {
        const response = await axios.get(`${API}/csr`);
        if (response.data.length === 0) {
          await axios.post(`${API}/seed-csr`);
          const newResponse = await axios.get(`${API}/csr`);
          setCsrItems(newResponse.data);
        } else {
          setCsrItems(response.data);
        }
      } catch (error) {
        console.error('Error fetching CSR:', error);
      }
    };
    fetchCSR();
  }, []);

  useEffect(() => {
    document.title = 'Club JCI Ltd - CSR & Impact';
  }, []);

  const pillars = [
    { icon: Heart, title: 'Strategic Philanthropy', desc: 'Empowering underserved communities' },
    { icon: Leaf, title: 'Environmental Stewardship', desc: 'Championing sustainability' },
    { icon: Users, title: 'Impactful Projects', desc: 'Creating real-world solutions' },
  ];

  return (
    <div data-testid="csr-page" className="pt-24">
      {/* Hero Section */}
      <section className="section-padding marble-overlay" data-testid="csr-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-[#D4AF37] text-sm uppercase tracking-widest mb-4">CSR & Impact</p>
            <h1 className="text-4xl sm:text-5xl font-semibold text-[#F1F5F9] mb-6">
              Creating <span className="gold-text">Lasting Impact</span>
            </h1>
            <p className="text-lg text-[#94A3B8] leading-relaxed">
              At Club JCI, we believe in giving back. Our commitment to social responsibility 
              extends beyond our walls to create meaningful change in our community.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pillars */}
      <section className="section-padding bg-[#0A192F]" data-testid="csr-pillars">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pillars.map((pillar, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass-card p-8 rounded-sm text-center"
              >
                <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <pillar.icon className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-semibold text-[#F1F5F9] mb-2">{pillar.title}</h3>
                <p className="text-[#94A3B8]">{pillar.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Initiatives */}
      <section className="section-padding" data-testid="csr-initiatives">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-[#F1F5F9] mb-4">
              Our <span className="gold-text">Initiatives</span>
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              Discover how we're making a difference in our community.
            </p>
          </motion.div>

          {csrItems.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {csrItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="glass-card rounded-sm overflow-hidden"
                  data-testid={`csr-item-${item.id}`}
                >
                  {item.image_url && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={getImageUrl(item.image_url)}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-[#F1F5F9] mb-2">{item.title}</h3>
                    <p className="text-sm text-[#94A3B8]">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <div className="text-6xl mb-6">💚</div>
              <h3 className="text-xl font-semibold text-[#F1F5F9] mb-2">Initiatives Coming Soon</h3>
              <p className="text-[#94A3B8]">
                We're preparing impactful initiatives that will be shared here soon.
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Get Involved */}
      <section className="section-padding bg-[#0A192F]" data-testid="get-involved">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-[#F1F5F9] mb-6">
              Get Involved
            </h2>
            <p className="text-[#94A3B8] mb-10 max-w-2xl mx-auto">
              As a member of Club JCI, you have the opportunity to participate in and 
              contribute to our CSR initiatives. Together, we can create a lasting positive 
              impact on our community.
            </p>
            <div className="glass-card p-8 rounded-sm">
              <p className="text-[#F1F5F9] italic font-accent text-xl mb-4">
                "The best way to find yourself is to lose yourself in the service of others."
              </p>
              <p className="text-[#D4AF37]">— Mahatma Gandhi</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
